import React, { Component, createRef } from "react";
import AlertContainer from "react-alert";
import { connect } from "react-redux";
import { Button, Checkbox, Form, Header, Message, Modal, Segment, Icon } from "semantic-ui-react";
import { faFileInvoiceDollar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation } from "react-i18next";

import { updateDealer as globalDealerUpdate } from "../../modules/App/store";
import { CustomConfirm, UserInput, ExactSearch } from "../../components";

import Service from "./service";

class DealerInvoicingSettings extends Component {
  state = {
    requiredFieldsError: false,
    accountModalOpen: false,
    emailInvalid: false,
    isSavingExactAccount: false,
    isLinkingExactAccount: false,
    isRetrievingAccount: false,
    exactAccountId: "",
    failedInvoiceIdToRetry: null,
    failedInvoiceIdToDelete: null,
    isRetryingFailedInvoices: false,
    isDeletingFailedInvocies: false,
  };

  msg = createRef();
  modalMsg = createRef();

  componentDidMount() {
    this.setState({ exactAccountId: this.props.dealer.exact_account_id || "" });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.dealer.id !== this.props.dealer.id) this.setState({ exactAccountId: this.props.dealer.exact_account_id || "" });
  }

  handleInputChange = (_e, { name, value }) => {
    const { dealer, onUpdateDealer } = this.props;

    onUpdateDealer({ ...dealer, [name]: value });
    this.setState({ requiredFieldsError: false, emailInvalid: false });
  };

  handleExactAccountIdChange = (_e, { value }) => {
    this.setState({ exactAccountId: value });
  };

  handleCheckboxChange = async (_e, { name, checked }) => {
    const { dealer, onUpdateDealer } = this.props;

    onUpdateDealer({ ...dealer, [name]: checked });
  };

  handleSelectAccountID = exactAccountId => {
    this.setState({ exactAccountId }, () => {
      this.linkAccount();
    });
  };

  handleOpenAccountModal = () => {
    const { dealer, onUpdateDealer } = this.props;

    if (!dealer.exact_account_id) {
      this.setState({ accountModalOpen: true });
      return;
    }

    this.setState({ isRetrievingAccount: true }, async () => {
      try {
        const response = await Service.retrieveInvoicingAccount({ dealer_id: dealer.id });

        if (!response?.data?.data) throw Error("Failed to retrieve invoicing account");

        const invoicingAccount = response.data.data;

        const exactDealerAccount = {
          exact_invoicing_company_name: invoicingAccount.Name,
          exact_invoicing_email: invoicingAccount.Contact.Email,
          exact_vat_number: invoicingAccount.VATNumber,
          exact_invoicing_address: invoicingAccount.AddressLine1,
          exact_invoicing_zip_code: invoicingAccount.Postcode,
          exact_invoicing_city: invoicingAccount.City,
          exact_invoicing_initials: invoicingAccount.Contact.Initials,
          exact_invoicing_last_name: invoicingAccount.Contact.LastName,
          exact_invoicing_country: invoicingAccount.Country,
          exact_chamber_of_commerce: invoicingAccount.ChamberOfCommerce,
          exact_invoicing_language: invoicingAccount.Language,
          exact_account_code: invoicingAccount.Code,
        };

        onUpdateDealer({ ...dealer, ...exactDealerAccount });
        this.setState({ isRetrievingAccount: false, accountModalOpen: true });
      } catch (error) {
        const errorMsg = typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? "Failed to retrieve invoicing account";
        this.modalMsg.current.show(errorMsg, { type: "error" });
        this.setState({ isRetrievingAccount: false });
      }
    });
  };

  validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  saveAccount = () => {
    const { dealer, onUpdateDealer } = this.props;
    const requiredFields = [
      "exact_invoicing_company_name",
      "exact_invoicing_email",
      "exact_vat_number",
      "exact_invoicing_address",
      "exact_invoicing_country",
      "exact_invoicing_zip_code",
      "exact_invoicing_city",
      "exact_invoicing_initials",
      "exact_invoicing_last_name",
      "exact_chamber_of_commerce",
      "exact_invoicing_language",
    ];

    const missingFields = requiredFields.some(field => !dealer[field]);

    if (missingFields) {
      this.setState({ requiredFieldsError: true });
      return;
    }

    if (!this.validateEmail(dealer.exact_invoicing_email)) {
      this.setState({ emailInvalid: true });
      return;
    }

    this.setState({ isSavingExactAccount: true }, async () => {
      try {
        const response = await Service.saveInvoicingAccount({
          exact_invoicing_company_name: dealer.exact_invoicing_company_name,
          exact_invoicing_email: dealer.exact_invoicing_email,
          exact_vat_number: dealer.exact_vat_number,
          exact_invoicing_address: dealer.exact_invoicing_address,
          exact_invoicing_zip_code: dealer.exact_invoicing_zip_code,
          exact_invoicing_city: dealer.exact_invoicing_city,
          exact_invoicing_initials: dealer.exact_invoicing_initials,
          exact_invoicing_last_name: dealer.exact_invoicing_last_name,
          exact_invoicing_country: dealer.exact_invoicing_country,
          exact_chamber_of_commerce: dealer.exact_chamber_of_commerce,
          exact_invoicing_language: dealer.exact_invoicing_language,
          exact_account_code: dealer.exact_account_code,
          dealer_id: dealer.id,
        });

        const updatedDealer = {
          ...dealer,
          exact_account_id: response?.data?.data?.exact_account_id || "",
        };

        onUpdateDealer(updatedDealer);
        this.props.globalDealerUpdate(updatedDealer);
        this.setState({ accountModalOpen: false, isSavingExactAccount: false, exactAccountId: "" });
      } catch (error) {
        const errorMsg = typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? "Failed to create invoicing account";

        this.modalMsg.current.show(errorMsg, { type: "error" });
        this.setState({ isSavingExactAccount: false });
      }
    });
  };

  linkAccount = () => {
    const { dealer, onUpdateDealer } = this.props;
    const { exactAccountId } = this.state;

    this.setState({ isLinkingExactAccount: true }, async () => {
      try {
        await Service.linkInvoicingAccount({ dealer_id: dealer.id, account_id: exactAccountId });

        const updatedDealer = {
          ...dealer,
          exact_account_id: exactAccountId,
        };
        onUpdateDealer(updatedDealer);
        this.props.globalDealerUpdate(updatedDealer);
        this.setState({ isLinkingExactAccount: false, exactAccountId: "" });
      } catch (error) {
        const errorMsg = typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? "Failed to link invoicing account";

        this.msg.current.show(errorMsg, { type: "error" });
        this.setState({ isLinkingExactAccount: false });
      }
    });
  };

  closeAccountModal = () => {
    this.setState({ accountModalOpen: false, requiredFieldsError: false });
  };

  renderModalAlert = () => {
    const props = {
      offset: 20,
      position: "top center",
      theme: "light",
      time: 10000,
      transition: "fade",
    };

    return (
      <div className="react-alert-container">
        <AlertContainer ref={this.modalMsg} {...props} />
      </div>
    );
  };

  renderAlert = () => {
    const props = {
      offset: 20,
      position: "top right",
      theme: "light",
      time: 10000,
      transition: "fade",
    };

    return (
      <div className="react-alert-container">
        <AlertContainer ref={this.msg} {...props} />
      </div>
    );
  };

  handleOpenRetryExactInvoicesCustomConfirm = failedInvoiceIdToRetry => {
    this.setState({ failedInvoiceIdToRetry });
  };

  handleCloseRetryExactInvoicesCustomConfirm = () => {
    this.setState({ failedInvoiceIdToRetry: null });
  };

  handleOpenDeleteExactInvoicesCustomConfirm = failedInvoiceIdToDelete => {
    this.setState({ failedInvoiceIdToDelete });
  };

  handleCloseDeleteExactInvoicesCustomConfirm = () => {
    this.setState({ failedInvoiceIdToDelete: null });
  };

  handleRetryExactInvoices = () => {
    const { dealer, onUpdateDealer, globalDealerUpdate } = this.props;

    this.setState({ isRetryingFailedInvoices: true }, () => {
      Service.retryExactInvoice({ dealer_id: dealer.id })
        .then(() => {
          const updatedDealer = { ...dealer, has_failed_invoices: false };

          onUpdateDealer(updatedDealer);
          globalDealerUpdate(updatedDealer);
        })
        .catch(error => {
          const errorMsg = typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? "Error Retrying Failed Invoicing";

          console.log("Error Retrying Failed Exact Invocing", error);
          this.modalMsg.current.show(errorMsg, { type: "error" });
        })
        .finally(() => {
          this.setState({ isRetryingFailedInvoices: false, failedInvoiceIdToRetry: null });
        });
    });
  };

  handleDeleteExactInvoices = () => {
    const { location, onLocationChange, globalLocationUpdate } = this.props;

    this.setState({ isDeletingFailedInvocies: true }, () => {
      Service.deleteFailedInvoice({ dealer_location_id: location.id })
        .then(() => {
          const updatedLocation = { ...location, has_failed_invoices: false };

          onLocationChange(updatedLocation);
          globalLocationUpdate(updatedLocation);
        })
        .catch(error => {
          const errorMsg = typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? "Error Retrying Failed Invoicing";

          console.log("Error Deleting Failed Exact Invocing", error);
          this.modalMsg.current.show(errorMsg, { type: "error" });
        })
        .finally(() => {
          this.setState({ isRetryingFailedInvoices: false, failedInvoiceIdToRetry: null });
        });
    });
  };

  render() {
    const { dealer, t } = this.props;
    const {
      requiredFieldsError,
      accountModalOpen,
      exactAccountId,
      emailInvalid,
      isSavingExactAccount,
      isRetrievingAccount,
      isLinkingExactAccount,
      failedInvoiceIdToRetry,
      failedInvoiceIdToDelete,
      isRetryingFailedInvoices,
      isDeletingFailedInvocies,
    } = this.state;

    const areFailedInvocesActionsDisabled = !dealer.has_failed_invoices || isRetryingFailedInvoices || isDeletingFailedInvocies;

    return (
      <>
        <Segment>
          <Header as="h5">
            <Header.Content className="DealerInvoicingSettings__header">
              <div>
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                {t("invoicing_settings").message || "Invoicing Settings"}
              </div>

              <div className="header-checkbox">
                <label htmlFor="is_exact_invoicing_enabled">{t("enable_dealer_level_invoicing").message || "Enable Dealer Level Invoicing"}</label>
                <Checkbox toggle name="is_exact_invoicing_enabled" checked={dealer.is_exact_invoicing_enabled} onChange={this.handleCheckboxChange} />
              </div>
            </Header.Content>
          </Header>

          {dealer.is_exact_invoicing_enabled && (
            <Segment>
              <Form>
                <Form.Field className="full-width-input" required>
                  <label>{t("exact_account_id").message || "Exact Account ID"}</label>
                  <UserInput
                    value={dealer.exact_account_id || exactAccountId}
                    placeholder={t("exact_account_id").message || "Exact Account ID"}
                    onChange={this.handleExactAccountIdChange}
                    readOnly={Boolean(dealer.exact_account_id)}
                  />
                </Form.Field>

                <div className="ExactInvoicing__action-group">
                  <div className="ExactInvoicing__action-group-buttons">
                    <Button onClick={this.handleOpenAccountModal} color="green" loading={isRetrievingAccount} disabled={isRetrievingAccount}>
                      {dealer.exact_account_id
                        ? t("update_exact_account").message || "Update Exact Account"
                        : t("create_exact_account").message || "Create Exact Account"}
                    </Button>

                    <ExactSearch setExactAccountID={this.handleSelectAccountID} dealerId={dealer.id} />

                    <Button
                      onClick={this.linkAccount}
                      loading={isLinkingExactAccount}
                      disabled={dealer.exact_account_id || isLinkingExactAccount || !exactAccountId}
                      color="green"
                    >
                      {t("save").message || "Save"}
                    </Button>

                    <Button
                      className="retry-invoicing-button"
                      icon
                      labelPosition="left"
                      disabled={areFailedInvocesActionsDisabled}
                      onClick={() => this.handleOpenRetryExactInvoicesCustomConfirm(dealer.id)}
                    >
                      <Icon name="refresh" color="green" />
                      {t("retry_sending_invoice").message || "Retry sending invoice"}
                    </Button>

                    <Button
                      icon
                      labelPosition="left"
                      disabled={areFailedInvocesActionsDisabled}
                      onClick={() => this.handleOpenDeleteExactInvoicesCustomConfirm(dealer.id)}
                      className="invoicing-button"
                    >
                      <Icon name="trash" color="red" />
                      {t("delete_failed_invoice").message || "Delete failed invoice"}
                    </Button>
                  </div>
                </div>
              </Form>

              <CustomConfirm
                type="warning"
                isOpen={failedInvoiceIdToRetry}
                handleConfirm={this.handleRetryExactInvoices}
                handleCancel={this.handleCloseRetryExactInvoicesCustomConfirm}
                confirmMsg={t("confirm_retry_failed_invoce").message || "Are you sure that you want to retry this invoice?"}
                isLoading={isRetryingFailedInvoices}
              />

              <CustomConfirm
                type="danger"
                isOpen={failedInvoiceIdToDelete}
                handleConfirm={this.handleDeleteExactInvoices}
                handleCancel={this.handleCloseDeleteExactInvoicesCustomConfirm}
                confirmMsg={t("confirm_delete_failed_invoce").message || "Are you sure that you want to delete this invoice?"}
                isLoading={isDeletingFailedInvocies}
              />
            </Segment>
          )}
        </Segment>

        <Modal open={accountModalOpen} onClose={() => this.setState({ accountModalOpen: false })} size="small">
          <Modal.Header>
            {dealer.exact_account_id ? t("update_exact_account").message || "Update Exact Account" : t("create_exact_account").message || "Create Exact Account"}
          </Modal.Header>
          <Modal.Content className="ExactModal__content">
            <Form>
              <Form.Group widths="equal">
                <Form.Field required error={requiredFieldsError && !dealer.exact_invoicing_company_name}>
                  <label>{t("company_name").message || "Company name"}</label>
                  <UserInput
                    name="exact_invoicing_company_name"
                    placeholder={t("company_name").message || "Company name"}
                    value={dealer.exact_invoicing_company_name}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>

                <Form.Field required error={requiredFieldsError && !dealer.exact_invoicing_country}>
                  <label>{t("exact_country").message || "Country"}</label>
                  <UserInput
                    name="exact_invoicing_country"
                    placeholder={t("exact_country").message || "Country"}
                    value={dealer.exact_invoicing_country}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Field required error={requiredFieldsError && !dealer.exact_invoicing_email}>
                  <label>{t("invoice_email").message || "Invoice Email"}</label>
                  <UserInput
                    name="exact_invoicing_email"
                    placeholder={t("invoice_email").message || "Invoice Email"}
                    value={dealer.exact_invoicing_email}
                    onChange={this.handleInputChange}
                  />
                  {emailInvalid && (
                    <Message negative size="tiny">
                      <p>{t("email_address_invalid").message || "Invalid email address"}</p>
                    </Message>
                  )}
                </Form.Field>

                <Form.Field required error={requiredFieldsError && !dealer.exact_invoicing_city}>
                  <label>{t("city").message || "City"}</label>
                  <UserInput
                    name="exact_invoicing_city"
                    placeholder={t("city").message || "City"}
                    value={dealer.exact_invoicing_city}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Field required error={requiredFieldsError && !dealer.exact_vat_number}>
                  <label>{t("vat_number").message || "VAT-Number"}</label>
                  <UserInput
                    name="exact_vat_number"
                    placeholder={t("vat_number").message || "VAT-Number"}
                    value={dealer.exact_vat_number}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>

                <Form.Field required error={requiredFieldsError && !dealer.exact_invoicing_address}>
                  <label>{t("address").message || "Adress"}</label>
                  <UserInput
                    name="exact_invoicing_address"
                    placeholder={t("address").message || "Address"}
                    value={dealer.exact_invoicing_address}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Field className="recipient-field" required>
                  <label>{t("invoice_recipient").message || "Invoice recipient"}</label>
                  <Form.Group>
                    <Form.Field width={8} error={requiredFieldsError && !dealer.exact_invoicing_initials}>
                      <UserInput
                        name="exact_invoicing_initials"
                        placeholder={t("initials").message || "Initials"}
                        value={dealer.exact_invoicing_initials}
                        onChange={this.handleInputChange}
                      />
                    </Form.Field>
                    <Form.Field width={8} error={requiredFieldsError && !dealer.exact_invoicing_last_name}>
                      <UserInput
                        name="exact_invoicing_last_name"
                        placeholder={t("surname").message || "Surname"}
                        value={dealer.exact_invoicing_last_name}
                        onChange={this.handleInputChange}
                      />
                    </Form.Field>
                  </Form.Group>
                </Form.Field>

                <Form.Field required error={requiredFieldsError && !dealer.exact_invoicing_zip_code}>
                  <label>{t("zip_code").message || "Zip code"}</label>
                  <UserInput
                    name="exact_invoicing_zip_code"
                    placeholder={t("zip_code").message || "Zip code"}
                    value={dealer.exact_invoicing_zip_code}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Group>
                <Form.Field width={8} required error={requiredFieldsError && !dealer.exact_chamber_of_commerce}>
                  <label>{t("chamber_of_commerce").message || "Chamber of Commerce"}</label>

                  <UserInput
                    name="exact_chamber_of_commerce"
                    placeholder={t("chamber_of_commerce").message || "Chamber of Commerce"}
                    value={dealer.exact_chamber_of_commerce}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>

                <Form.Field width={8} required error={requiredFieldsError && !dealer.exact_invoicing_language}>
                  <label>{t("language").message || "Language"}</label>

                  <UserInput
                    name="exact_invoicing_language"
                    placeholder={t("language").message || "Language"}
                    value={dealer.exact_invoicing_language}
                    onChange={this.handleInputChange}
                  />
                </Form.Field>
              </Form.Group>

              {dealer.exact_account_code && (
                <Form.Group widths="equal">
                  <Form.Field>
                    <label>{t("account_code").message || "Account Code"}</label>
                    <UserInput
                      readOnly
                      name="exact_account_code"
                      placeholder={t("account_code").message || "Account Code"}
                      value={dealer.exact_account_code}
                      onChange={this.handleInputChange}
                    />
                  </Form.Field>
                </Form.Group>
              )}

              {requiredFieldsError && (
                <Message negative>
                  <p>{t("required_fields_error").message || "Please fill all required fields"}</p>
                </Message>
              )}
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.closeAccountModal} color="grey">
              {t("cancel").message || "Cancel"}
            </Button>
            <Button onClick={this.saveAccount} loading={isSavingExactAccount} disabled={isSavingExactAccount} color="green">
              {t("save").message || "Save"}
            </Button>
          </Modal.Actions>
          {this.renderModalAlert()}
        </Modal>

        {this.renderAlert()}
      </>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    globalDealerUpdate: dealer => dispatch(globalDealerUpdate(dealer)),
  };
};

export default connect(null, mapDispatchToProps)(withTranslation()(DealerInvoicingSettings));
